/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\PickupGroupGeneralBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Applications module.
----------------------------------------------------------------*/


import { Globe } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import Toggle from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select-shadcn';
import { DEFAULT_SETTINGS } from '../utils/formDefaults';

interface PickupGroupData {
    name: string;
    skipBusy: boolean;
    priority: number;
    enabled: boolean;
    settings: {
        pickup_tone: string;
        ringback: string;
        pickup_timeout: number;
        confirmation: boolean;
        DND_override: boolean;
    };
}

interface PickupGroupGeneralBoxProps {
    formData: PickupGroupData;
    errors: Record<string, string>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (field: string, value: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSettingChange: (key: string, value: any) => void;
    onSubmit: () => void;
    t: (key: string) => string;
}

export function PickupGroupGeneralBox({ formData, errors, onChange, onSettingChange, onSubmit, t }: PickupGroupGeneralBoxProps) {
    return (
        <FormBox title={t('pickupGroups.sections.generalInfo')} icon={<Globe className="h-5 w-5" />}>
            <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                {/* Name */}
                <Label htmlFor="name">
                    <SimpleTooltip content={t('pickupGroups.fields.name.unique')}>
                        <span>
                            {t('pickupGroups.fields.name.label')}
                            <span className="text-red-500 ml-1">*</span>
                        </span>
                    </SimpleTooltip>
                </Label>
                <div className="col-span-3">
                    <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => onChange('name', e.target.value)}
                        placeholder={t('pickupGroups.fields.name.placeholder')}
                        className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name}</p>}
                </div>

                {/* Pickup Tone */}
                <Label htmlFor="pickup_tone">
                    <SimpleTooltip content={t('pickupGroups.fields.pickupTone.tooltip')}>
                        {t('pickupGroups.fields.pickupTone.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Select value={formData.settings.pickup_tone} onValueChange={(v) => onSettingChange('pickup_tone', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="default">{t('pickupGroups.fields.pickupTone.options.default')}</SelectItem>
                            <SelectItem value="beep">{t('pickupGroups.fields.pickupTone.options.beep')}</SelectItem>
                            <SelectItem value="short_beep">{t('pickupGroups.fields.pickupTone.options.short_beep')}</SelectItem>
                            <SelectItem value="none">{t('pickupGroups.fields.pickupTone.options.none')}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Ringback */}
                <Label htmlFor="ringback">
                    <SimpleTooltip content={t('pickupGroups.fields.ringback.tooltip')}>
                        {t('pickupGroups.fields.ringback.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Select value={formData.settings.ringback} onValueChange={(v) => onSettingChange('ringback', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="local">{t('pickupGroups.fields.ringback.options.local')}</SelectItem>
                            <SelectItem value="us-ring">{t('pickupGroups.fields.ringback.options.us-ring')}</SelectItem>
                            <SelectItem value="uk-ring">{t('pickupGroups.fields.ringback.options.uk-ring')}</SelectItem>
                            <SelectItem value="none">{t('pickupGroups.fields.ringback.options.none')}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Pickup Timeout */}
                <Label htmlFor="pickup_timeout">
                    <SimpleTooltip content={t('pickupGroups.fields.pickupTimeout.tooltip')}>
                        {t('pickupGroups.fields.pickupTimeout.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Input
                        id="pickup_timeout"
                        type="number" min={5} max={120}
                        value={formData.settings.pickup_timeout}
                        onChange={(e) => onSettingChange('pickup_timeout', parseInt(e.target.value, 10) || DEFAULT_SETTINGS.pickup_timeout)}
                        className={errors.pickupTimeout ? 'border-red-500' : ''}
                    />
                    {errors.pickupTimeout && <p className="text-sm text-red-500 mt-1">{errors.pickupTimeout}</p>}
                </div>

                {/* Confirmation */}
                <Label htmlFor="confirmation">
                    <SimpleTooltip content={t('pickupGroups.fields.confirmation.tooltip')}>
                        {t('pickupGroups.fields.confirmation.label')}
                    </SimpleTooltip>
                </Label>
                <div><Toggle value={formData.settings.confirmation} onChange={(v) => onSettingChange('confirmation', v)} /></div>

                {/* DND Override */}
                <Label htmlFor="dnd_override">
                    <SimpleTooltip content={t('pickupGroups.fields.dndOverride.tooltip')}>
                        {t('pickupGroups.fields.dndOverride.label')}
                    </SimpleTooltip>
                </Label>
                <div><Toggle value={formData.settings.DND_override} onChange={(v) => onSettingChange('DND_override', v)} /></div>

                {/* Skip Busy */}
                <Label htmlFor="skip_busy">
                    <SimpleTooltip content={t('pickupGroups.fields.skipBusy.tooltip')}>
                        {t('pickupGroups.fields.skipBusy.label')}
                    </SimpleTooltip>
                </Label>
                <div><Toggle value={formData.skipBusy} onChange={(v) => onChange('skipBusy', v)} /></div>

                {/* Priority */}
                <Label htmlFor="priority">
                    <SimpleTooltip content={t('pickupGroups.fields.priority.tooltip')}>
                        {t('pickupGroups.fields.priority.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Input
                        id="priority"
                        type="number" min={1} max={9999}
                        value={formData.priority}
                        onChange={(e) => onChange('priority', parseInt(e.target.value, 10) || 100)}
                        className={errors.priority ? 'border-red-500' : ''}
                    />
                    {errors.priority && <p className="text-sm text-red-500 mt-1">{errors.priority}</p>}
                </div>

                {/* Enabled */}
                <Label htmlFor="enabled">
                    <SimpleTooltip content={t('pickupGroups.fields.enabled.tooltip')}>
                        {t('pickupGroups.fields.enabled.label')}
                    </SimpleTooltip>
                </Label>
                <div><Toggle value={formData.enabled} onChange={(v) => onChange('enabled', v)} /></div>
            </form>
        </FormBox>
    );
}
